import {
    ArrayParser,
    BooleanParser,
    DateParser,
    NumberParser,
    ObjectParser,
    ParamParser,
    parseQueryParams,
    StringParser,
    serializeQueryParams,
} from "@resamare/functions";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { InferType, ObjectSchema } from "yup";
import * as yup from "yup";

/**
 *
 * Extrait les parsers à utiliser à partir d'un schema yup
 *
 * @param {ObjectSchema<T>} schema - Schéma Yup à analyser.
 * @returns {Record<string, ParamParser>} - Mapping des clés du schéma vers leurs parseurs correspondants.
 */
function getParsersFromSchema<T extends yup.AnyObject>(schema: ObjectSchema<T>) {
    const parserMap: Record<string, ParamParser> = {};

    Object.entries(schema.fields).forEach(([key, field]) => {
        const typedField = field as yup.Schema;

        if (typedField instanceof yup.ArraySchema) {
            const innerType = typedField.innerType?.describe().type;

            switch (innerType) {
                case "string":
                    parserMap[key] = ArrayParser(StringParser);
                    break;
                case "number":
                    parserMap[key] = ArrayParser(NumberParser);
                    break;
                case "boolean":
                    parserMap[key] = ArrayParser(BooleanParser);
                    break;
                case "date":
                    parserMap[key] = ArrayParser(DateParser);
                    break;
                case "object":
                    parserMap[key] = ArrayParser(ObjectParser);
                    break;
                default:
                    parserMap[key] = ArrayParser(StringParser);
            }
        } else {
            switch (typedField.type) {
                case "string":
                    parserMap[key] = StringParser;
                    break;
                case "number":
                    parserMap[key] = NumberParser;
                    break;
                case "boolean":
                    parserMap[key] = BooleanParser;
                    break;
                case "date":
                    parserMap[key] = DateParser;
                    break;
                case "object":
                    parserMap[key] = ObjectParser;
                    break;
                default:
                    parserMap[key] = StringParser;
            }
        }
    });

    return parserMap;
}

/**
 * `useQueryParams` - Hook pour gérer les query params avec typage basé sur un schéma Yup.
 *
 * Fonctionnalités :
 * - **Parsage automatique** des query params selon le schéma fourni.
 * - **Validation** des données via Yup.
 * - **Mise à jour des query params** avec sérialisation automatique.
 *
 * @param schema - Schéma Yup définissant la structure et les types des query params.
 * @returns `{ parsedQuery, validQuery, validQueryOrNull,  setQuery, updateQuery }`
 * - `parsedQuery` : Objet contenant les query params parsés avec les types définis dans le schéma.
 * - `validQuery` : Query params validés selon le schéma.
 * - `validQueryOrNull` : Query params validés selon le schéma, ou `null` si la validation échoue.
 * - `setQuery` : Fonction permettant de remplacer les query params.
 * - `updateQuery` : Fonction permettant de mettre à jour les query params partiellement.
 */
export function useQueryParams<T extends yup.AnyObject>(schema: ObjectSchema<T>) {
    const [searchParams, setSearchParams] = useSearchParams();

    const parsers = useMemo(() => getParsersFromSchema(schema), [schema]);

    const parsedQuery = useMemo(
        () => parseQueryParams(searchParams, parsers),
        [searchParams, parsers],
    ) as Partial<InferType<typeof schema>>;

    const setQuery = useCallback(
        (data: Partial<InferType<typeof schema>>) => {
            setSearchParams(serializeQueryParams(data));
        },
        [setSearchParams],
    );

    const updateQuery = useCallback(
        (data: Partial<InferType<typeof schema>>) => {
            setSearchParams(serializeQueryParams({ ...parsedQuery, ...data }));
        },
        [setSearchParams, parsedQuery],
    );

    const validQueryOrNull = useMemo(() => {
        try {
            return schema.validateSync(parsedQuery, { abortEarly: false }) as InferType<
                typeof schema
            >;
        } catch {
            return null;
        }
    }, [parsedQuery, schema]);

    const queryObject = useMemo(
        () => ({
            parsedQuery,
            validQueryOrNull,
            setQuery,
            updateQuery,
            get validQuery() {
                if (!validQueryOrNull) throw new Error("Invalid query params");
                return validQueryOrNull;
            },
        }),
        [parsedQuery, validQueryOrNull, setQuery, updateQuery],
    );

    return queryObject;
}
